// src/pages/WorkspacePage.js - Workspace(폴더 목록) 전용 페이지

import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useAppContext } from "../context/AppContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsisV,
  faFolder,
  faSpinner,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import B2BLayout from "../components/b2b/B2BLayout";
import B2BPageShell, { B2BPageFooterAction } from "../components/b2b/B2BPageShell";
import { DeleteConfirmModal, Toast } from "../components/ui";
import UpgradeModal from "../components/UpgradeModal";
import CreateFolderModal from "../components/CreateFolderModal";
import "./WorkspacePage.scss";

const WorkspacePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const Layout = B2BLayout;
  
  const {
    projects,
    deleteProject,
    loadProjects,
    projectsLoading,
    canCreateProject,
    isLoggedIn,
    sessionChecked,
  } = useAppContext();

  const [showCreateModal, setShowCreateModal] = useState(false);

  // [추가] 프로젝트 카드 메뉴 상태 (어떤 카드의 메뉴가 열려있는지)
  const [openMenuProjectId, setOpenMenuProjectId] = useState(null);

  // [추가] 삭제 확인 모달 상태
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(null); // { id, name, studyCount }
  // 삭제 완료 토스트 상태
  const [deleteSuccessToast, setDeleteSuccessToast] = useState({
    isOpen: false,
    projectName: ''
  });

  // [추가] 업그레이드 모달 상태
  const [upgradeModal, setUpgradeModal] = useState({
    isOpen: false,
    reason: 'project'  // 'project' | 'study' | 'feature'
  });

  useEffect(() => {
    if (!sessionChecked) return;
    if (!isLoggedIn) return;
    loadProjects();
  }, [isLoggedIn, sessionChecked, loadProjects]);

  // 라우트 변경 시 모달 상태 초기화
  useEffect(() => {
    setShowCreateModal(false);
  }, [location.pathname]);

  /**
   * [프로젝트 생성 모달 열기 핸들러]
   * - Tier 제한을 먼저 확인하고 모달 표시
   */
  const handleOpenCreateModal = async () => {
    const loadedProjects = await loadProjects();
    if (!Array.isArray(loadedProjects)) return;
    if (!canCreateProject()) {
      setUpgradeModal({ isOpen: true, reason: 'project' });
      return;
    }
    setShowCreateModal(true);
  };

  const handleProjectClick = (projectSlug) => {
    navigate(`/project/${projectSlug}`);
  };

  /**
   * [프로젝트 삭제 모달 열기]
   * - 케밥 메뉴에서 삭제 클릭 시 모달 표시
   */
  const openDeleteConfirm = (projectId, projectName, e) => {
    e.stopPropagation();

    // projects에서 직접 조사 개수 가져오기 (별도 fetch 불필요)
    const project = projects.find(p => p.id === projectId);
    const studyCount = (project && project.studies) ? project.studies.length : 0;

    setDeleteConfirmModal({ id: projectId, name: projectName, studyCount });
    setOpenMenuProjectId(null); // 메뉴 닫기
  };

  /**
   * [프로젝트 삭제 실행]
   * - 모달에서 확인 버튼 클릭 시 실행
   * - Supabase에서 삭제 (CASCADE로 관련 데이터 자동 삭제)
   * - 삭제 후 전체 프로젝트/스터디 재로드 (tier 제한 체크를 위해)
   */
  const handleDeleteProject = async () => {
    if (!deleteConfirmModal) return;

    const projectName = deleteConfirmModal.name;
    await deleteProject(deleteConfirmModal.id);
    setDeleteConfirmModal(null); // 모달 닫기
    
    // ⭐ 전체 프로젝트와 스터디 재로드 (tier 제한이 즉시 반영됨)
    await loadProjects({ force: true });
    
    // 삭제 완료 토스트 표시
    setDeleteSuccessToast({
      isOpen: true,
      projectName: projectName
    });
  };

  const projectsGrid = (
    <div className="projects-grid">
      {projectsLoading ? (
        <div className="projects-loading">
          <FontAwesomeIcon icon={faSpinner} spin />
          <span>프로젝트를 불러오는 중...</span>
        </div>
      ) : projects.length > 0 ? (
        projects.map((project) => {
          return (
            <div
              key={project.id}
              className="project-card"
              onClick={() => handleProjectClick(project.slug)}
            >
              {/* [추가] 프로젝트 카드 우상단 메뉴 (⋮) */}
              <div className="project-card-menu">
                <button
                  className="project-menu-btn"
                  onClick={(e) => {
                    e.stopPropagation(); // 카드 클릭 방지
                    setOpenMenuProjectId(
                      openMenuProjectId === project.id ? null : project.id
                    );
                  }}
                >
                  <FontAwesomeIcon icon={faEllipsisV} />
                </button>
                {/* [드롭다운] 삭제 메뉴 */}
                {openMenuProjectId === project.id && (
                  <div className="project-dropdown-menu">
                    <button
                      className="project-menu-item delete"
                      onClick={(e) => openDeleteConfirm(project.id, project.name, e)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      <span>폴더 삭제</span>
                    </button>
                  </div>
                )}
              </div>

              <div className="project-card-header">
                <h3>{project.name}</h3>
              </div>
              {/* [태그] DB 컬럼명: keywords (배열), 개수 제한 없음 */}
              <div className="project-tags">
                {project.keywords &&
                  project.keywords.map((tag, index) => (
                    <span key={index} className="project-tag">
                      {tag}
                    </span>
                  ))}
              </div>
              <div className="project-stats">
                <span>
                  {(project.studies && project.studies.length) || 0}개의 리서치
                </span>
              </div>
            </div>
          );
        })
      ) : (
        <div className="empty-projects">
          <FontAwesomeIcon icon={faFolder} className="empty-icon" />
          <h3>폴더가 없습니다</h3>
          <p>첫 번째 폴더를 만들어 시작해보세요.</p>
        </div>
      )}
    </div>
  );

  const pageModals = (
    <>
      <CreateFolderModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreateSuccess={(createdProject) => {
          setShowCreateModal(false);
          if (createdProject && createdProject.slug) {
            navigate(`/project/${createdProject.slug}`);
          }
        }}
      />

      {/* 삭제 확인 모달 */}
      <DeleteConfirmModal
        isOpen={!!deleteConfirmModal}
        title="폴더 삭제"
        itemName={deleteConfirmModal?.name || ''}
        warningItems={
          deleteConfirmModal
            ? [
                '폴더',
                `폴더 내 ${deleteConfirmModal.studyCount ?? 0}개의 리서치`,
                '리서치 내 모든 산출물 (계획서, 스크리닝 설문, 진행 가이드라인)',
                '이와 관련된 모든 데이터'
              ]
            : []
        }
        onConfirm={handleDeleteProject}
        onCancel={() => setDeleteConfirmModal(null)}
      />

      <Toast
        isOpen={deleteSuccessToast.isOpen}
        type="success"
        message={`"${deleteSuccessToast.projectName}" 폴더를 삭제했습니다.`}
        onClose={() => setDeleteSuccessToast({ isOpen: false, projectName: '' })}
      />

      {/* 업그레이드 모달 */}
      <UpgradeModal
        isOpen={upgradeModal.isOpen}
        reason={upgradeModal.reason}
        onClose={() => setUpgradeModal({ isOpen: false, reason: 'project' })}
      />
    </>
  );

  return (
    <Layout hideCollapseButton={true} defaultCollapsed={false} hidePageHeader={true}>
      <B2BPageShell
        className="research-page"
        title="Research"
        description="비즈니스/서비스 폴더를 생성하고, AI가 최적의 리서치를 제시합니다."
        contentClassName="research-page-content"
        footer={
          <B2BPageFooterAction
            leftIcon={<Plus size={24} strokeWidth={1.8} />}
            onClick={handleOpenCreateModal}
          >
            새 폴더 생성
          </B2BPageFooterAction>
        }
      >
        {projectsGrid}
      </B2BPageShell>
      {pageModals}
    </Layout>
  );
};

export default WorkspacePage;
